import {useState, useEffect, useCallback} from 'react'

/**
 * @name useScrollAtTop
 *
 * @description
 * Custom hook that detects if the window is scrolled to the top.
 *
 * @hook
 * @returns {boolean} A boolean value indicating whether the window is scrolled to the top.
 *
 * @example
 * ```jsx
 * const MyComponent = () => {
 *  const isAtTop = useScrollAtTop();
 *
 *  return (
 *  <div>
 *  {isAtTop ? 'You are at the top!' : 'You are not at the top.'}
 *  </div>
 *  );
 * };
 * ```
 */
const useScrollAtTop = (): boolean => {
    const [isAtTop, setIsAtTop] = useState(true)

    const handleScroll = useCallback(() => {
        setIsAtTop(window.scrollY <= 0)
    }, [])

    useEffect(() => {
        if (typeof window !== 'undefined') setIsAtTop(window.scrollY <= 0)

        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [handleScroll])

    return isAtTop
}

export {useScrollAtTop}
